import { useEffect, useState } from 'react'
import { View, Text, Pressable, Modal, ScrollView, StyleSheet } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useTranslation } from 'react-i18next'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
import { supabase } from '../../lib/supabase'
import { fonts } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'
import { showConfirm } from '../../lib/platform'
import RivalryCard from './RivalryCard'

// Dettaglio di una sfida: punteggio, gara per gara, e le azioni (chiudi la sfida / rivincita)
export default function RivalrySheet({ row, meId, avatars, canRematch, onClose, onEnd, onRematch }) {
  const tc = useThemeColors()
  const { t, i18n } = useTranslation()
  const insets = useSafeAreaInsets()
  const [results, setResults] = useState(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!row) return
    setResults(null)
    supabase.rpc('fn_rivalry_results', { p_id: row.id }).then(({ data }) => setResults(data ?? []))
  }, [row?.id])

  if (!row) return null
  const mine = row.id_challenger === meId || row.id_rival === meId
  const otherId = row.id_challenger === meId ? row.id_rival : row.id_challenger
  const otherName = otherId === row.id_rival ? row.rival_name : row.challenger_name
  const nameOf = (id) => (id === row.id_challenger ? row.challenger_name : row.rival_name)
  const fmt = (d) => d ? new Date(d).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' }) : ''

  const doEnd = async () => {
    if (!await showConfirm(t('rivalry.end_confirm', { name: otherName }))) return
    setBusy(true); await onEnd(row.id); setBusy(false); onClose()
  }
  const doRematch = async () => { setBusy(true); const ok = await onRematch(otherId, otherName); setBusy(false); if (ok) onClose() }

  return (
    <Modal visible transparent animationType="slide" onRequestClose={onClose}>
      <View style={s.overlay}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onClose} accessibilityRole="button" accessibilityLabel={t('pick.close')} />
        <View style={[s.sheet, { backgroundColor: tc.bgCard, paddingBottom: insets.bottom + 12 }]}>
          <View style={[s.header, { borderBottomColor: tc.border }]}>
            <Text style={[s.title, { color: tc.text }]} accessibilityRole="header">{t('rivalry.detail_title')}</Text>
            <Pressable onPress={onClose} hitSlop={6} style={[s.closeBtn, { backgroundColor: tc.bgCard2 }]} accessibilityRole="button" accessibilityLabel={t('pick.close')}>
              <MaterialCommunityIcons name="close" size={18} color={tc.text} />
            </Pressable>
          </View>
          <ScrollView contentContainerStyle={{ padding: 16, gap: 14 }}>
            <RivalryCard row={row} meId={meId} avatars={avatars} onPress={() => {}} />
            <Text style={[s.section, { color: tc.textMuted }]}>{t('rivalry.results_title')}</Text>
            {results === null ? (
              <Text style={[s.note, { color: tc.textMuted }]}>{t('common.loading')}</Text>
            ) : results.length === 0 ? (
              <Text style={[s.note, { color: tc.textMuted }]}>{t('rivalry.no_results')}</Text>
            ) : results.map(r => (
              <View key={r.id_race} style={[s.resRow, { borderBottomColor: tc.border }]}>
                <View style={{ flex: 1 }}>
                  <Text style={[s.resRace, { color: tc.text }]} numberOfLines={2}>{r.race_name}</Text>
                  <Text style={[s.resDate, { color: tc.textMuted }]}>{fmt(r.race_date)}</Text>
                </View>
                <Text style={[s.resWin, { color: r.winner === meId ? tc.rosaText : tc.text }]} numberOfLines={1}>
                  {t('rivalry.point_to', { name: nameOf(r.winner) })}
                </Text>
              </View>
            ))}
            {mine && row.status === 'active' && (
              <Pressable onPress={doEnd} disabled={busy} accessibilityRole="button" style={[s.btn, { borderColor: tc.border, opacity: busy ? 0.5 : 1 }]}>
                <Text style={[s.btnText, { color: tc.textMuted }]}>{t('rivalry.end_cta')}</Text>
              </Pressable>
            )}
            {mine && row.status === 'ended' && canRematch && (
              <Pressable onPress={doRematch} disabled={busy} accessibilityRole="button" style={[s.btn, { borderColor: tc.rosaBorder, backgroundColor: tc.rosaSubtle, opacity: busy ? 0.5 : 1 }]}>
                <Text style={[s.btnText, { color: tc.rosaText }]}>{t('rivalry.rematch')}</Text>
              </Pressable>
            )}
          </ScrollView>
        </View>
      </View>
    </Modal>
  )
}

const s = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'flex-end' },
  sheet: { borderTopLeftRadius: 20, borderTopRightRadius: 20, maxHeight: '85%' },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottomWidth: 0.5 },
  title: { fontFamily: fonts.condensedBold, fontSize: 20 },
  closeBtn: { width: 44, height: 44, borderRadius: 22, alignItems: 'center', justifyContent: 'center' },
  section: { fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, fontFamily: fonts.condensedBold },
  note: { fontSize: 13, textAlign: 'center', paddingVertical: 8 },
  resRow: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 8, minHeight: 48, borderBottomWidth: 0.5 },
  resRace: { fontSize: 14, fontFamily: fonts.condensedBold },
  resDate: { fontSize: 12 },
  resWin: { fontSize: 13, fontFamily: fonts.condensedBold, maxWidth: '45%', textAlign: 'right' },
  btn: { minHeight: 48, borderWidth: 1, borderRadius: 12, alignItems: 'center', justifyContent: 'center' },
  btnText: { fontFamily: fonts.condensedBold, fontSize: 14 },
})
